import { type IPartyCustomization } from '../interface/ICustomization';

// Dark ink used both as the default page text colour and as the "on brand"
// choice when a party colour is light enough that white would fail contrast.
const DARK_INK = '#1c2a20';
const WHITE = '#ffffff';

interface RGB {
	r: number;
	g: number;
	b: number;
}

// Inline custom properties this module writes onto <html>. Kept in one place so
// clearPartyTheme() can remove exactly what applyPartyTheme() set — letting the
// :root defaults (and the [data-theme='dark'] remap) reassert.
const MANAGED_COLOR_PROPS = [
	'--color-brand-primary',
	'--color-brand-primary-hover',
	'--color-brand-primary-dark',
	'--color-brand-primary-light',
	'--color-brand-secondary',
	'--color-brand-accent',
	'--color-brand-mint',
	'--color-on-brand',
];

// Page-background image props, managed independently of the brand colours (a
// party may publish a backdrop without valid colours). Consumed by the
// full-page `body::before` backdrop layer in index.css.
const MANAGED_BG_PROPS = ['--party-bg-image', '--party-bg-active'];

// Coerce the app's placeholder strings ("null"/"undefined") and blanks to
// absent, mirroring the string-clean pattern used elsewhere for API values.
const cleanCustomString = (value: string | null | undefined): string | null => {
	if (typeof value !== 'string') return null;
	const trimmed = value.trim();
	if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') return null;
	return trimmed;
};

// Sets (or clears) the page-background custom properties from the party's
// backgroundImageUrl. Kept separate from the colour logic so it runs even when
// the party has no valid primary colour.
const applyPartyBackground = (customization: IPartyCustomization | null): void => {
	const root = document.documentElement;
	const url = cleanCustomString(customization?.backgroundImageUrl);
	if (url) {
		// Strip characters that could break out of the CSS url("…") token.
		const safe = url.replace(/["\\\n\r]/g, '');
		root.style.setProperty('--party-bg-image', `url("${safe}")`);
		root.style.setProperty('--party-bg-active', '1');
	} else {
		MANAGED_BG_PROPS.forEach((prop) => root.style.removeProperty(prop));
	}
};

export const hexToRgb = (hex: string): RGB | null => {
	if (typeof hex !== 'string') return null;
	const cleaned = hex.replace('#', '').trim();
	const full =
		cleaned.length === 3
			? cleaned
					.split('')
					.map((c) => c + c)
					.join('')
			: cleaned;
	if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
	const num = parseInt(full, 16);
	return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};

const clampChannel = (c: number): number => Math.max(0, Math.min(255, Math.round(c)));

const rgbToHex = ({ r, g, b }: RGB): string => '#' + [r, g, b].map((c) => clampChannel(c).toString(16).padStart(2, '0')).join('');

const channelLuminance = (channel: number): number => {
	const s = channel / 255;
	return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

export const relativeLuminance = (hex: string): number => {
	const rgb = hexToRgb(hex);
	if (!rgb) return 0;
	return 0.2126 * channelLuminance(rgb.r) + 0.7152 * channelLuminance(rgb.g) + 0.0722 * channelLuminance(rgb.b);
};

export const contrastRatio = (a: string, b: string): number => {
	const la = relativeLuminance(a);
	const lb = relativeLuminance(b);
	const lighter = Math.max(la, lb);
	const darker = Math.min(la, lb);
	return (lighter + 0.05) / (darker + 0.05);
};

// Mixes `hex` toward white by `amt` (0–1).
export const lighten = (hex: string, amt: number): string => {
	const rgb = hexToRgb(hex);
	if (!rgb) return hex;
	const t = Math.max(0, Math.min(1, amt));
	return rgbToHex({
		r: rgb.r + (255 - rgb.r) * t,
		g: rgb.g + (255 - rgb.g) * t,
		b: rgb.b + (255 - rgb.b) * t,
	});
};

// Mixes `hex` toward black by `amt` (0–1).
export const darken = (hex: string, amt: number): string => {
	const rgb = hexToRgb(hex);
	if (!rgb) return hex;
	const t = Math.max(0, Math.min(1, amt));
	return rgbToHex({ r: rgb.r * (1 - t), g: rgb.g * (1 - t), b: rgb.b * (1 - t) });
};

// Picks white or dark ink for text/icons sitting on top of `hex`, whichever
// gives the stronger WCAG contrast — so brand buttons/hero copy stay legible.
export const onBrandColor = (hex: string): string =>
	contrastRatio(hex, WHITE) >= contrastRatio(hex, DARK_INK) ? WHITE : DARK_INK;

// Deep brand colours (e.g. a near-black navy) disappear against the dark-mode
// page background, so lift them toward a legible tone. Already-light colours are
// left alone (the clamp keeps the lift proportional to how dark the input is).
const adaptForDark = (hex: string): string => {
	const lum = relativeLuminance(hex);
	if (lum >= 0.35) return hex;
	// Map luminance 0 → 0.5 lift, 0.35 → 0 lift.
	const amount = Math.max(0, Math.min(0.5, (0.35 - lum) * 1.4));
	return lighten(hex, amount);
};

/**
 * Applies a party's brand colours as inline custom properties on <html>,
 * deriving hover/dark/light shades plus a legible `--color-on-brand`. Invalid or
 * missing colours fall back to clearing (defaults reassert). Re-run whenever the
 * resolved theme flips so deep colours are re-adapted for the dark surface.
 */
export const applyPartyTheme = (customization: IPartyCustomization | null, resolvedTheme: 'light' | 'dark'): void => {
	if (typeof document === 'undefined') return;

	const root = document.documentElement;

	// The page-background image is independent of the brand colours — apply it
	// before the colour-validity gate so a backdrop still shows when a party has
	// supplied one without valid colours.
	applyPartyBackground(customization);

	const rawPrimary = customization?.primaryColor ?? null;
	if (!rawPrimary || !hexToRgb(rawPrimary)) {
		MANAGED_COLOR_PROPS.forEach((prop) => root.style.removeProperty(prop));
		return;
	}

	const isDark = resolvedTheme === 'dark';
	const primary = isDark ? adaptForDark(rawPrimary) : rawPrimary;

	const rawSecondary =
		customization?.secondaryColor && hexToRgb(customization.secondaryColor) ? customization.secondaryColor : null;
	const secondaryBase = rawSecondary ?? darken(rawPrimary, 0.12);
	const secondary = isDark ? adaptForDark(secondaryBase) : secondaryBase;

	root.style.setProperty('--color-brand-primary', primary);
	root.style.setProperty('--color-brand-primary-hover', isDark ? lighten(primary, 0.1) : darken(primary, 0.08));
	root.style.setProperty('--color-brand-primary-dark', darken(primary, 0.18));
	// The "light" token is a subtle tinted surface (used for hover fills). On dark
	// mode that means a deep tint of the primary rather than a pale wash.
	root.style.setProperty('--color-brand-primary-light', isDark ? darken(primary, 0.55) : lighten(primary, 0.85));
	root.style.setProperty('--color-brand-secondary', secondary);

	if (customization?.accentColor && hexToRgb(customization.accentColor)) {
		root.style.setProperty('--color-brand-accent', customization.accentColor);
	} else {
		root.style.removeProperty('--color-brand-accent');
	}

	root.style.setProperty('--color-brand-mint', lighten(primary, isDark ? 0.35 : 0.55));
	// On-brand is computed from the *displayed* primary so buttons/hero text using
	// the current primary stay legible in whichever theme is active.
	root.style.setProperty('--color-on-brand', onBrandColor(primary));
};

/** Removes every inline property applyPartyTheme() may have set. */
export const clearPartyTheme = (): void => {
	if (typeof document === 'undefined') return;
	const root = document.documentElement;
	[...MANAGED_COLOR_PROPS, ...MANAGED_BG_PROPS].forEach((prop) => root.style.removeProperty(prop));
};

export default applyPartyTheme;
